import React, { useEffect, useRef, useState } from 'react';

export interface MapProperty {
  id: number;
  lat: number;
  lng: number;
  name: string;
  address: string;
  phone: string;
  description: string;
  url: string;
  /** Google Maps directions/place URL, derived via getPropertyAddressUrl(). */
  directionsUrl: string;
}

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface CityGoogleMapProps {
  properties: MapProperty[];
  apiKey: string;
  highlightId?: number | null;
  /** Bounds to set as the initial viewport. If omitted, fits to all properties. */
  initialBounds?: MapBounds;
  /** Called when the viewport changes (pan/zoom/resize). */
  onBoundsChanged?: (bounds: MapBounds) => void;
}

interface Cluster {
  center: { lat: number; lng: number };
  properties: MapProperty[];
}

// ─── Marker Icons ──────────────────────────────────────────────────────────────

const SINGLE_MARKER_SVG = (color: string, borderColor: string) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
  <circle cx="17" cy="17" r="15" fill="${color}" stroke="${borderColor}" stroke-width="2.5"/>
  <circle cx="17" cy="17" r="5" fill="#fff"/>
</svg>`)}`;

function createClusterIcon(count: number): string {
  const size = count >= 100 ? 76 : count >= 50 ? 68 : count >= 20 ? 60 : count >= 10 ? 52 : 46;
  const fontSize = count >= 100 ? 15 : count >= 10 ? 14 : 13;
  const r = size / 2;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
  <circle cx="${r}" cy="${r}" r="${r}" fill="rgba(180,30,30,0.08)"/>
  <circle cx="${r}" cy="${r}" r="${r * 0.82}" fill="rgba(180,30,30,0.15)"/>
  <circle cx="${r}" cy="${r}" r="${r * 0.64}" fill="rgba(180,30,30,0.28)"/>
  <circle cx="${r}" cy="${r}" r="${r * 0.48}" fill="rgba(190,35,35,0.88)" stroke="rgba(255,255,255,0.5)" stroke-width="1"/>
  <text x="${r}" y="${r}" text-anchor="middle" dy="0.36em"
        font-family="Arial,Helvetica,sans-serif" font-weight="700" font-size="${fontSize}" fill="#fff">
    ${count}
  </text>
</svg>`)}`;
}

// ─── Clustering Algorithm ──────────────────────────────────────────────────────

function computeClusters(
  properties: MapProperty[],
  map: any,
  gridPixels: number,
): Cluster[] {
  const google = (window as any).google;
  const projection = map.getProjection?.();
  if (!projection || properties.length === 0) {
    return properties.map((p) => ({
      center: { lat: p.lat, lng: p.lng },
      properties: [p],
    }));
  }

  const zoom = map.getZoom();
  const scale = Math.pow(2, zoom);

  function toPixel(lat: number, lng: number) {
    const pt = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
    return { x: pt.x * scale, y: pt.y * scale };
  }

  const working: {
    center: { lat: number; lng: number };
    px: { x: number; y: number };
    props: MapProperty[];
  }[] = [];

  for (const prop of properties) {
    const px = toPixel(prop.lat, prop.lng);
    let merged = false;

    for (const cl of working) {
      const dx = cl.px.x - px.x;
      const dy = cl.px.y - px.y;
      if (Math.sqrt(dx * dx + dy * dy) < gridPixels) {
        cl.props.push(prop);
        const n = cl.props.length;
        cl.center = {
          lat: cl.props.reduce((s, p) => s + p.lat, 0) / n,
          lng: cl.props.reduce((s, p) => s + p.lng, 0) / n,
        };
        cl.px = toPixel(cl.center.lat, cl.center.lng);
        merged = true;
        break;
      }
    }

    if (!merged) {
      working.push({
        center: { lat: prop.lat, lng: prop.lng },
        px: toPixel(prop.lat, prop.lng),
        props: [prop],
      });
    }
  }

  return working.map((c) => ({ center: c.center, properties: c.props }));
}

// ─── Spiderfier (max-zoom overlap handling) ────────────────────────────────────

function spiderfyMarkers(
  clusterProperties: MapProperty[],
  center: { lat: number; lng: number },
  map: any,
  google: any,
  infoWindow: any,
  markersArray: any[],
) {
  const count = clusterProperties.length;
  const angleStep = (2 * Math.PI) / count;
  const radiusLat = 0.0003;
  const radiusLng = 0.0004;

  clusterProperties.forEach((prop, i) => {
    const angle = angleStep * i - Math.PI / 2;
    const offsetLat = center.lat + radiusLat * Math.sin(angle);
    const offsetLng = center.lng + radiusLng * Math.cos(angle);

    const legLine = new google.maps.Polyline({
      path: [center, { lat: offsetLat, lng: offsetLng }],
      strokeColor: 'rgba(157,16,16,0.35)',
      strokeWeight: 1.5,
      map,
      zIndex: 0,
    });

    const marker = new google.maps.Marker({
      map,
      position: { lat: offsetLat, lng: offsetLng },
      icon: {
        url: SINGLE_MARKER_SVG('#1a1a1a', '#fff'),
        scaledSize: new google.maps.Size(30, 30),
        anchor: new google.maps.Point(15, 15),
      },
      animation: google.maps.Animation.DROP,
      zIndex: 5,
    });

    marker.addListener('click', () => {
      infoWindow.setContent(buildInfoWindowContent(prop));
      infoWindow.open(map, marker);
    });

    (marker as any)._propId = prop.id;
    (marker as any)._isSpider = true;
    (marker as any)._spiderLeg = legLine;
    markersArray.push(marker);
  });
}

function clearSpiderfiedMarkers(markersArray: any[]) {
  const kept: any[] = [];
  for (const m of markersArray) {
    if ((m as any)._isSpider) {
      m.setMap(null);
      (m as any)._spiderLeg?.setMap(null);
    } else {
      kept.push(m);
    }
  }
  markersArray.length = 0;
  markersArray.push(...kept);
}

// ─── InfoWindow Builder ────────────────────────────────────────────────────────

function buildInfoWindowContent(prop: MapProperty): string {
  return `
    <div class="case-map-info-window">
      <p class="case-map-info-title"><a href="${prop.url}">${prop.name}</a></p>
      <div class="case-map-info-address"><a href="${prop.directionsUrl}" target="_blank" rel="noopener noreferrer">${prop.address}</a></div>
      <div class="case-map-info-phone"><a href="tel:${prop.phone.replace(/[^0-9]/g, '')}">${prop.phone}</a></div>
      ${prop.description ? `<div class="case-map-info-desc">${prop.description}</div>` : ''}
      <a href="${prop.url}" class="case-map-info-cta">Go To Property</a>
    </div>`;
}

// ─── Google Maps Loader ────────────────────────────────────────────────────────

let googleMapsLoaded = false;
let googleMapsLoading: Promise<void> | null = null;

function loadGoogleMaps(apiKey: string): Promise<void> {
  if (googleMapsLoaded) return Promise.resolve();
  if (googleMapsLoading) return googleMapsLoading;

  googleMapsLoading = new Promise((resolve, reject) => {
    if ((window as any).google?.maps) {
      googleMapsLoaded = true;
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?v=3.exp&key=${apiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      googleMapsLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });

  return googleMapsLoading;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const GRID_PIXELS = 85;
const MAX_SPIDER_ZOOM = 17;

export function CityGoogleMap({ properties, apiKey, highlightId, initialBounds, onBoundsChanged }: CityGoogleMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);
  const propsRef = useRef<MapProperty[]>(properties);
  const [ready, setReady] = useState(false);
  const debounceRef = useRef<any>(null);
  const listenersRef = useRef<any[]>([]);
  const initializedRef = useRef(false);

  // Keep refs current without re-attaching listeners
  propsRef.current = properties;
  const onBoundsChangedRef = useRef(onBoundsChanged);
  onBoundsChangedRef.current = onBoundsChanged;
  const initialBoundsRef = useRef(initialBounds);

  // ── Load Google Maps API ────────────────────────────────────────────────

  useEffect(() => {
    if (!apiKey) return;
    loadGoogleMaps(apiKey)
      .then(() => setReady(true))
      .catch(console.error);
  }, [apiKey]);

  // ── Render clusters on current map viewport ──────────────────────────────

  const renderClustersRef = useRef<() => void>(() => {});
  renderClustersRef.current = () => {
    const map = mapRef.current;
    const google = (window as any).google;
    if (!map || !google?.maps) return;

    const props = propsRef.current;

    if (infoWindowRef.current) infoWindowRef.current.close();

    clearSpiderfiedMarkers(markersRef.current);
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    if (props.length === 0) return;

    const clusters = computeClusters(props, map, GRID_PIXELS);

    clusters.forEach((cluster) => {
      if (cluster.properties.length === 1) {
        const prop = cluster.properties[0];
        const marker = new google.maps.Marker({
          map,
          position: { lat: prop.lat, lng: prop.lng },
          icon: {
            url: SINGLE_MARKER_SVG('#1a1a1a', '#fff'),
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 16),
          },
          optimized: true,
          zIndex: 2,
        });

        marker.addListener('click', () => {
          infoWindowRef.current.setContent(buildInfoWindowContent(prop));
          infoWindowRef.current.open(map, marker);
          map.panTo(marker.getPosition()!);
        });

        (marker as any)._propId = prop.id;
        (marker as any)._isCluster = false;
        markersRef.current.push(marker);
      } else {
        const count = cluster.properties.length;
        const size =
          count >= 100 ? 76 : count >= 50 ? 68 : count >= 20 ? 60 : count >= 10 ? 52 : 46;

        const marker = new google.maps.Marker({
          map,
          position: cluster.center,
          icon: {
            url: createClusterIcon(count),
            scaledSize: new google.maps.Size(size, size),
            anchor: new google.maps.Point(size / 2, size / 2),
          },
          optimized: true,
          zIndex: 10 + count,
          cursor: 'pointer',
        });

        const clusterProps = cluster.properties;
        const clusterCenter = { ...cluster.center };

        marker.addListener('click', () => {
          const currentZoom = map.getZoom();

          if (currentZoom >= MAX_SPIDER_ZOOM) {
            clearSpiderfiedMarkers(markersRef.current);
            spiderfyMarkers(
              clusterProps,
              clusterCenter,
              map,
              google,
              infoWindowRef.current,
              markersRef.current,
            );
            return;
          }

          const bounds = new google.maps.LatLngBounds();
          clusterProps.forEach((p) =>
            bounds.extend(new google.maps.LatLng(p.lat, p.lng)),
          );
          map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
        });

        (marker as any)._isCluster = true;
        (marker as any)._clusterProps = clusterProps;
        markersRef.current.push(marker);
      }
    });
  };

  // ── Initialize map (runs once) ─────────────────────────────────────────

  useEffect(() => {
    if (!containerRef.current || !ready || mapRef.current) return;
    const google = (window as any).google;
    if (!google?.maps) return;

    const ib = initialBoundsRef.current;
    const props = propsRef.current;

    // Determine initial center
    let center: { lat: number; lng: number };
    if (ib) {
      center = { lat: (ib.north + ib.south) / 2, lng: (ib.east + ib.west) / 2 };
    } else if (props.length > 0) {
      const b = new google.maps.LatLngBounds();
      props.forEach((p) => b.extend(new google.maps.LatLng(p.lat, p.lng)));
      center = { lat: b.getCenter().lat(), lng: b.getCenter().lng() };
    } else {
      center = { lat: 36.15, lng: -95.99 };
    }

    mapRef.current = new google.maps.Map(containerRef.current, {
      zoom: 5,
      center,
      disableDefaultUI: true,
      zoomControl: true,
    });

    infoWindowRef.current = new google.maps.InfoWindow();

    // Re-cluster on zoom change
    const zoomListener = mapRef.current.addListener('zoom_changed', () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => renderClustersRef.current(), 180);
    });
    listenersRef.current.push(zoomListener);

    // Report viewport bounds on idle (fires after every pan/zoom settles)
    const idleListener = mapRef.current.addListener('idle', () => {
      const b = mapRef.current?.getBounds();
      if (b && onBoundsChangedRef.current) {
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        onBoundsChangedRef.current({
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng(),
        });
      }
    });
    listenersRef.current.push(idleListener);

    // Set initial viewport
    const map = mapRef.current;
    if (ib) {
      const bounds = new google.maps.LatLngBounds(
        { lat: ib.south, lng: ib.west },
        { lat: ib.north, lng: ib.east },
      );
      map.fitBounds(bounds, 60);
    } else if (props.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      props.forEach((p) => bounds.extend(new google.maps.LatLng(p.lat, p.lng)));
      map.fitBounds(bounds, 60);
    } else if (props.length === 1) {
      map.setCenter({ lat: props[0].lat, lng: props[0].lng });
      map.setZoom(14);
    }

    // Render clusters + mark ready once map settles
    google.maps.event.addListenerOnce(map, 'idle', () => {
      initializedRef.current = true;
      renderClustersRef.current();
    });
  }, [ready]);

  // ── Re-render clusters when properties change (filter updates) ─────────

  useEffect(() => {
    if (!mapRef.current || !ready || !initializedRef.current) return;
    renderClustersRef.current();
  }, [properties, ready]);

  // ── Highlight a specific property (from card pin click) ────────────────

  useEffect(() => {
    if (highlightId == null || !mapRef.current || !ready) return;
    const google = (window as any).google;

    const prop = properties.find((p) => p.id === highlightId);
    if (!prop) return;

    mapRef.current.setZoom(16);
    mapRef.current.panTo({ lat: prop.lat, lng: prop.lng });

    google.maps.event.addListenerOnce(
      mapRef.current,
      'idle',
      () => {
        setTimeout(() => {
          const marker = markersRef.current.find(
            (m) => (m as any)._propId === highlightId && !(m as any)._isCluster,
          );
          if (marker) {
            google.maps.event.trigger(marker, 'click');
          }
        }, 120);
      },
    );
  }, [highlightId, ready, properties]);

  // ── Cleanup ────────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      listenersRef.current.forEach((l) => {
        const google = (window as any).google;
        google?.maps?.event?.removeListener(l);
      });
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────

  if (!apiKey) {
    return (
      <div
        className="case-map-holder"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
        }}
      >
        Map requires API key
      </div>
    );
  }

  return <div ref={containerRef} className="case-map-holder" />;
}

export default CityGoogleMap;
